/**
 * /execution/governance — read-only enforcement-mode + override audit.
 *
 * Surfaces the canonical mode + audit trail. Mode flips still happen through
 * the canonical mode-flip writer in the existing super-admin flow
 * (Control Tower) — this page does NOT expose that writer.
 */
import { useMemo } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import {
  getNetworkEnforcementMode,
  getEnforcementBehavior,
  getEnforcementModeAudit,
  getEnforcementModeBanner,
} from "@/data/contractor/networkExecution/enforcementMode";

export default function ExecutionGovernancePage() {
  const { role } = useRole();
  const mode = getNetworkEnforcementMode();
  const behavior = useMemo(() => getEnforcementBehavior(mode), [mode]);
  const audit = useMemo(() => [...getEnforcementModeAudit()].reverse(), []);
  const banner = getEnforcementModeBanner();

  if (!canAccessExecutionDomain(role)) return null;

  return (
    <CockpitPage
      
      title="Governance"
      maxWidth="7xl"
      controls={
        <Button asChild variant="outline" size="sm">
          <Link to="/execution/control-tower">Open Control Tower</Link>
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current mode</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xl font-bold capitalize">{mode}</span>
            <Badge variant="outline" className="text-[10px]">{behavior.escalationChannel}</Badge>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Manual confirm: {behavior.requiresManualConfirmation ? "required" : "off"}
            {" · "}
            Auto side-effects: {behavior.autoExecutesSideEffects ? "on" : "off"}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recent flip banner</div>
          {banner ? (
            <>
              <div className="mt-1 text-sm font-semibold">→ {banner.mode}</div>
              <div className="text-[11px] text-muted-foreground">
                Flipped {format(new Date(banner.flippedAt), "MMM d, h:mm a")} · expires {format(new Date(banner.expiresAt), "MMM d, h:mm a")}
              </div>
            </>
          ) : (
            <div className="mt-1 text-[12px] text-muted-foreground">No active banner.</div>
          )}
        </Card>

        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Audit entries</div>
          <div className="mt-1 text-2xl font-bold tabular-nums">{audit.length}</div>
          <div className="text-[11px] text-muted-foreground">All-time mode flips</div>
        </Card>
      </div>

      <h2 className="text-[12px] font-bold uppercase tracking-[0.16em] text-muted-foreground mb-2">Audit trail</h2>
      {audit.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">No mode flips recorded.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <ul className="divide-y divide-border">
            {audit.map((a, i) => (
              <li key={i} className="px-4 py-2.5 flex items-center gap-3">
                <span className="text-[11px] text-muted-foreground whitespace-nowrap w-32">
                  {format(new Date(a.at), "MMM d, h:mm a")}
                </span>
                <Badge variant="outline" className="text-[10px]">{a.fromMode} → {a.toMode}</Badge>
                <span className="text-[11px] text-muted-foreground">{a.actorRole} · {a.actorId}</span>
                <span className="text-[12px] truncate">{a.reason}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </CockpitPage>
  );
}
